import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Plus, 
  Edit, 
  UserCheck, 
  UserX, 
  Shield,
  User,
  Mail,
  Calendar,
  Crown,
  Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MemberCRMModal from './MemberCRMModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const { token: authToken } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberCRM, setShowMemberCRM] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'member'
  });

  const userRoles = [
    { value: 'admin', label: 'Administrator', icon: Shield },
    { value: 'moderator', label: 'Moderator', icon: UserCheck },
    { value: 'member', label: 'Member', icon: User }
  ];

  const userStatuses = [
    { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: 'Inactive', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'suspended', label: 'Suspended', color: 'bg-red-100 text-red-800' }
  ];

  const membershipTiers = [
    { value: 'free', label: 'Free (Boot Camp)', color: 'bg-gray-100 text-gray-800', icon: User },
    { value: 'basic', label: 'Basic (Rookie)', color: 'bg-blue-100 text-blue-800', icon: Crown },
    { value: 'rookie_pro', label: 'Rookie Pro', color: 'bg-green-100 text-green-800', icon: Crown },
    { value: 'pro', label: 'Pro', color: 'bg-purple-100 text-purple-800', icon: Crown },
    { value: 'enterprise', label: 'Enterprise', color: 'bg-orange-100 text-orange-800', icon: Crown }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.full_name || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, formData, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success('User created successfully');
      setShowDialog(false);
      setFormData({ email: '', full_name: '', password: '', role: 'member' });
      fetchUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      const message = error.response?.data?.detail || 'Failed to create user';
      toast.error(message);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?status=${newStatus}`, {}, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      toast.success(`User status updated to ${newStatus}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleMembershipTierChange = async (userId, userEmail, newTier) => {
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/membership-tier`, {
        membership_tier: newTier
      }, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      toast.success(`Membership tier updated to ${newTier} for ${userEmail}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to update membership tier:', error);
      const message = error.response?.data?.detail || 'Failed to update membership tier';
      toast.error(message);
    }
  };

  const getRoleInfo = (role) => {
    return userRoles.find(r => r.value === role) || userRoles[2];
  };

  const getStatusInfo = (status) => {
    return userStatuses.find(s => s.value === status) || userStatuses[0];
  };

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setShowMemberCRM(true);
  };

  const handleCloseMemberCRM = () => {
    setShowMemberCRM(false);
    setSelectedMember(null);
  };

  const resetForm = () => {
    setFormData({ email: '', full_name: '', password: '', role: 'member' });
  };

  if (loading) {
    return (
      <AdminLayout title="User Management">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  const adminCount = users.filter(u => ['admin', 'moderator'].includes(u.role)).length;
  const memberCount = users.filter(u => u.role === 'member').length;
  const activeCount = users.filter(u => u.status === 'active').length;

  return (
    <AdminLayout title="User Management">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Users
            </CardTitle>
            <Users className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{users.length}</div>
          </CardContent>
        </Card>
        
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Administrators
            </CardTitle>
            <Shield className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{adminCount}</div>
          </CardContent>
        </Card>
        
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Members
            </CardTitle>
            <User className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{memberCount}</div>
          </CardContent>
        </Card>
        
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Active Users
            </CardTitle>
            <UserCheck className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{activeCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Header with Add Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
          <p className="text-sm text-gray-600">
            Manage user accounts and permissions
          </p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user account to the system
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  placeholder="Enter full name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({...formData, role: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select user role" />
                  </SelectTrigger>
                  <SelectContent>
                    {userRoles.map((role) => {
                      const Icon = role.icon;
                      return (
                        <SelectItem key={role.value} value={role.value}>
                          <div className="flex items-center">
                            <Icon className="h-4 w-4 mr-2" />
                            {role.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Accounts</CardTitle>
          <CardDescription>
            All registered users and their account details
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Membership Tier</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    const statusInfo = getStatusInfo(user.status);
                    const tierInfo = getTierInfo(user.membership_tier || 'free');
                    const RoleIcon = roleInfo.icon;
                    const TierIcon = tierInfo.icon;
                    
                    return (
                      <tr key={user.id}>
                        <td>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center font-bold text-black text-sm">
                              {user.full_name.charAt(0)}
                            </div>
                            <div className="ml-3">
                              <button
                                onClick={() => handleMemberClick(user)}
                                className="font-medium text-gray-900 hover:text-blue-600 transition-colors cursor-pointer underline decoration-dotted underline-offset-4"
                                title="Click to view member details"
                              >
                                {user.full_name}
                              </button>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Mail className="h-3 w-3 mr-1" />
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center">
                            <RoleIcon className="h-4 w-4 mr-2 text-gray-600" />
                            <span className="capitalize font-medium">{roleInfo.label}</span>
                          </div>
                        </td>
                        <td>
                          <div className="space-y-2">
                            <Badge className={tierInfo.color}>
                              <TierIcon className="h-3 w-3 mr-1" />
                              {tierInfo.label}
                            </Badge>
                            {user.role === 'member' && (
                              <Select 
                                value={user.membership_tier || 'free'} 
                                onValueChange={(value) => handleMembershipTierChange(user.id, user.email, value)}
                              >
                                <SelectTrigger className="w-40 h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {membershipTiers.map((tier) => {
                                    const TierSelectIcon = tier.icon;
                                    return (
                                      <SelectItem key={tier.value} value={tier.value}>
                                        <div className="flex items-center">
                                          <TierSelectIcon className="h-3 w-3 mr-2" />
                                          <span className="text-xs">{tier.label}</span>
                                        </div>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge className={statusInfo.color}>
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td>
                          <span className="text-sm text-gray-600">
                            {user.last_login 
                              ? new Date(user.last_login).toLocaleDateString()
                              : 'Never'
                            }
                          </span>
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            {user.status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(user.id, 'suspended')}
                                className="text-red-600 hover:text-red-800"
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(user.id, 'active')}
                                className="text-green-600 hover:text-green-800"
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
              <p className="text-gray-600 mb-4">
                Start by creating the first user account
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Member CRM Modal */}
      {showMemberCRM && selectedMember && (
        <MemberCRMModal 
          member={selectedMember}
          onClose={handleCloseMemberCRM}
        />
      )}
    </AdminLayout>
  );
};

export default AdminUsers;